import type { OnlineStore, SocialMediaAccount, Store } from 'src/types/business';
import type { Product } from 'src/types/product';

type PartnerSource =
  | ''
  | 'CURRENT_SELLER'
  | 'MAGAZINE'
  | 'NEWSPAPER'
  | 'FRIEND'
  | 'GOOGLE'
  | 'ANOTHER_WEBSITE'
  | 'CUSTOMER_EMAIL'
  | 'OTHER';

interface SimpleStoreCategory {
  uses: boolean;
}

interface StoreCategory extends SimpleStoreCategory {
  storesUsed: Store[];
}

interface OnlineStoreCategory {
  storesUsed: OnlineStore[];
  uses: boolean;
}

interface DeliverAreas {
  selfDelivery?: boolean;
  selfDeliveryAreas: string;
  uk?: boolean;
}

export enum AttachmentState {
  UPLOADING = 'UPLOADING',
  UPLOAD_COMPLETE = 'UPLOAD_COMPLETE',
  UPLOAD_ERROR = 'UPLOAD_ERROR',
}

export interface AttachmentUploadInProgress {
  attachmentId: string;
  attachmentState: AttachmentState.UPLOADING;
  label: string;
}

export interface AttachmentUploadComplete {
  attachmentId: string;
  attachmentState: AttachmentState.UPLOAD_COMPLETE;
  label: string;
  uploadReference: string;
}

export interface AttachmentUploadError {
  attachmentId: string;
  attachmentState: AttachmentState.UPLOAD_ERROR;
  label: string;
}

export type Attachment =
  | AttachmentUploadInProgress
  | AttachmentUploadComplete
  | AttachmentUploadError;

interface PipelinePlans {
  details: string;
  provided?: boolean;
}

interface Certification {
  attachments: Attachment[];
  required: YesNoUnknown;
}
export interface PostalAddress {
  addressLine1?: string;
  addressLine2?: string;
  addressTown?: string;
  country: string;
  postcode?: string;
}

export type DialingCode = '' | '44' | '353';

export interface Telephone {
  dialingCode: DialingCode;
  telephone?: string;
}

export type RegistrationType = '' | 'COMPANIES_HOUSE_REGISTERED' | 'SOLE_TRADER' | 'OTHER';
export type LeadInTime =
  | ''
  | 'LESS_THAN_ONE_MONTH'
  | 'LESS_THAN_THREE_MONTHS'
  | 'MORE_THAN_THREE_MONTHS';
export type YesNoUnknown = '' | 'TRUE' | 'FALSE' | 'UNKNOWN';
export type ProductCategories =
  | 'BABY_AND_CHILD'
  | 'CLOTHING'
  | 'EXPERIENCES'
  | 'FASHION_ACCESSORIES'
  | 'FOOD_AND_DRINK'
  | 'HOBBIES_LEISURE_AND_CRAFTING'
  | 'ELECTRONICS_AND_TECH_ACCESSORIES'
  | 'HEALTH_AND_BEAUTY'
  | 'HOME_ART_AND_OUTDOORS'
  | 'JEWELLERY'
  | 'STATIONERY_AND_PARTY_SUPPLIES'
  | 'TOYS_AND_GAMES'
  | 'PET_SUPPLIES';

export interface RegistrationForm {
  address: PostalAddress;
  certification: Certification;
  companyName: string;
  deliverAreas: DeliverAreas;
  instagramHandle: string;
  leadinTime: LeadInTime;
  packagingAttachments: Attachment[];
  partnerAgreesToProvideId: boolean;
  partnerInvited: boolean;
  partnerSource: PartnerSource;
  pipelinePlans: PipelinePlans;
  productCategories: ProductCategories[];
  products: Product[];
  registrationType: RegistrationType;
  salesChannels: {
    boutiques: SimpleStoreCategory;
    highstreetStores: StoreCategory;
    onlineStores: OnlineStoreCategory;
    supermarkets: StoreCategory;
  };
  socialMedia: SocialMediaAccount[];
  telephone: Telephone;
  website: string;
}
